import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { api } from "../../../../singleton/axios";
import {
  AiSummaryCheckResponse,
  GenerateAiSummaryResponse,
  AiEarningsSummary,
} from "../../../../types/AiEarningsSummary";
import { useTourMode } from "../../../../contexts/TourModeContext";
import { useAnalytics } from "../../../../hooks/useAnalytics";

/**
 * Hook to check if AI summary exists and get it if available
 * Enhanced with polling capability for generation in progress
 */
export function useAiEarningsSummary(
  symbol: string,
  year: number,
  quarter: number,
  options?: { enablePolling?: boolean },
) {
  const { isTourMode, getMockData } = useTourMode();
  const [isPolling, setIsPolling] = useState(options?.enablePolling ?? false);

  const query = useQuery<AiSummaryCheckResponse, Error>({
    queryKey: ["aiEarningsSummary", symbol, year, quarter, isTourMode],
    queryFn: async () => {
      // Return mock data in tour mode
      if (isTourMode) {
        const mockSummary = getMockData<AiEarningsSummary>("aiEarningsSummary");
        if (mockSummary) {
          return {
            exists: true,
            summary: mockSummary,
            symbol: mockSummary.symbol,
            period: mockSummary.period,
            year: mockSummary.year,
          };
        }
      }
      const { data } = await api.get<AiSummaryCheckResponse>(
        "/ai-earnings-summary",
        {
          params: {
            symbol,
            year: year.toString(),
            quarter: quarter.toString(),
          },
        },
      );
      return data;
    },
    enabled: isTourMode || !!(symbol && year && quarter),
    staleTime: isTourMode ? Infinity : 0, // Always refetch on mount so navigating back detects in-progress generations
    gcTime: 30 * 24 * 60 * 60 * 1000, // Keep in cache for 30 days (for instant display while background refetch runs)
    // Enable polling when requested (disabled in tour mode)
    refetchInterval: isTourMode ? false : isPolling ? 5000 : false,
    refetchIntervalInBackground: false, // Don't poll when window not focused
  });

  // Stop polling when summary is found
  useEffect(() => {
    if (query.data?.exists && isPolling) {
      setIsPolling(false);
    }
  }, [query.data?.exists, isPolling]);

  // Auto-start polling when the check response indicates generation is already in progress
  useEffect(() => {
    if (query.data?.isGenerating && !isPolling) {
      setIsPolling(true);
    }
  }, [query.data?.isGenerating, isPolling]);

  // Auto-timeout polling after 5 minutes
  useEffect(() => {
    if (isPolling) {
      const timeout = setTimeout(
        () => {
          setIsPolling(false);
        },
        5 * 60 * 1000,
      ); // 5 minutes

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isPolling]);

  const startPolling = () => {
    setIsPolling(true);
  };

  const stopPolling = () => {
    setIsPolling(false);
  };

  return {
    ...query,
    isPolling,
    startPolling,
    stopPolling,
  };
}

/**
 * Hook to generate AI summary with 202 handling and polling integration
 * Creates a unique mutation for each ticker/quarter combination
 */
export function useGenerateAiSummary(
  symbol: string,
  year: number,
  quarter: number,
) {
  const queryClient = useQueryClient();
  const { isTourMode } = useTourMode();
  const { trackFeature } = useAnalytics();
  const mutationKey = `generateAiSummary-${symbol}-${year}-${quarter}`;

  return useMutation<
    Omit<GenerateAiSummaryResponse, "summary"> & {
      // Absent until polling finds the finished summary, or when refused.
      summary?: AiEarningsSummary;
      needsPolling?: boolean;
      membershipRequired?: boolean;
    },
    Error,
    {
      onStartPolling?: () => void;
    }
  >({
    mutationKey: [mutationKey], // Unique key per ticker/quarter combination
    mutationFn: async ({ onStartPolling }) => {
      try {
        const response = await api.post<GenerateAiSummaryResponse>(
          "/ai-earnings-summary/generate",
          {
            symbol,
            year: year.toString(),
            quarter: quarter.toString(),
          },
        );

        // Success - generation completed immediately
        return response.data;
      } catch (error: any) {
        // Handle 202 responses (generation already in progress)
        if (error.response?.status === 202) {
          return {
            success: false,
            needsPolling: true,
            message: error.response.data.message,
            isGenerating: true,
            lockKey: error.response.data.lockKey, // Include for debugging
          };
        }

        // The server keeps generating and saves even when the connection is
        // cut under it — a network error, or an edge/proxy timeout (Cloudflare
        // 524 at 100s, 502/504) — so hand over to the GET poll instead of
        // reporting a failure the user would retry into a 202.
        const status: number | undefined = error.response?.status;
        if (!error.response || status === 502 || status === 504 || status === 524) {
          return {
            success: false,
            needsPolling: true,
            message: "Still generating — checking for the finished summary.",
            isGenerating: true,
          };
        }

        // Non-members can't generate (Basic and Pro both can). Returned rather
        // than thrown so the UI can offer the upgrade path, mirroring how
        // useStockAnalysisReport surfaces quotaExceeded.
        if (error.response?.status === 403) {
          trackFeature("earnings_report_membership_required", {
            ticker: symbol,
            year,
            quarter,
          });
          return {
            success: false,
            membershipRequired: true,
            message: error.response.data?.message,
            error: error.response.data?.error,
          };
        }

        // Re-throw other errors. Track for the generated -> viewed/failed funnel.
        trackFeature("earnings_report_failed", {
          ticker: symbol,
          year,
          quarter,
          error_status: error.response?.status,
        });
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // A generation actually ran (or is running) — not a membership refusal.
      if (!data.membershipRequired) {
        trackFeature("earnings_report_generated", {
          ticker: symbol,
          year,
          quarter,
        });
      }

      if (data.success && data.summary) {
        // Generation completed immediately - update cache
        // IMPORTANT: Query key must include isTourMode to match the useAiEarningsSummary query
        queryClient.setQueryData(
          ["aiEarningsSummary", symbol, year, quarter, isTourMode],
          {
            exists: true,
            summary: data.summary,
            symbol: symbol,
            period: `Q${quarter}`,
            year: year,
          },
        );
      } else if (data.needsPolling) {
        // Immediately refetch the check query so isGenerating: true is reflected without
        // waiting for the first poll interval
        queryClient.invalidateQueries({
          queryKey: ["aiEarningsSummary", symbol, year, quarter, isTourMode],
        });
        // Start polling for completion
        if (variables.onStartPolling) {
          variables.onStartPolling();
        }
      }
    },
  });
}
